import {
    IonAvatar,
    IonBadge,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonTitle,
    IonToolbar,
    RefresherEventDetail
} from "@ionic/react";
import {
    arrowBackOutline,
    americanFootballOutline,
    peopleOutline,
    refreshOutline,
    statsChartOutline
} from "ionicons/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
    CompetitionGame,
    GameDetail,
    GamePlay,
    PlayersById,
    TeamDetail,
    getGameDetail,
    getPlayers,
    getTeamDetail
} from "../services/mexfbaApi";
import * as Components from "./StatsFunction";
import "./TeamStats.css";

interface RouteParams {
    id: string;
}

type StatCategory =
    | "pase"
    | "carrera"
    | "recepcion"
    | "defensa"
    | "retornos"
    | "patadas";

interface PlayerStats {
    id: string;
    att: number;
    comp: number;
    ydsPas: number;
    tdsPas: number;
    int: number;
    qbr: number;
    attA: number;
    ydsAca: number;
    tdsAca: number;
    fumble: number;
    attR: number;
    compR: number;
    ydsRec: number;
    tdsRec: number;
    attK: number;
    ydsK: number;
    attP: number;
    ydsP: number;
    tdsK: number;
    tdsP: number;
}

interface DefensiveStats {
    id: string;
    tkl: number;
    assTkl: number;
    sack: number;
    asSack: number;
    int: number;
    ff: number;
    fr: number;
    td: number;
    pd: number;
    pb: number;
    tklL: number;
}

interface KickerStats {
    id: string;
    attFG: number;
    attXP: number;
    ydsFG: number;
    compFG: number;
    compXP: number;
    punt: number;
    ydsP: number;
    blqK: number;
    blqP: number;
}

interface TeamStatistics {
    offensive: PlayerStats[];
    defensive: DefensiveStats[];
    kickers: KickerStats[];
}

const categories: Array<{
    id: StatCategory;
    label: string;
}> = [
    { id: "pase", label: "Pase" },
    { id: "carrera", label: "Carrera" },
    { id: "recepcion", label: "Recepción" },
    { id: "defensa", label: "Defensa" },
    { id: "retornos", label: "Retornos" },
    { id: "patadas", label: "Patadas" }
];

function getGames(detail: TeamDetail | null): CompetitionGame[] {
    if (!detail?.Partidos) {
        return [];
    }

    const games = Array.isArray(detail.Partidos)
        ? detail.Partidos
        : Object.values(detail.Partidos);

    if (!detail.Info.comp_id) {
        return games;
    }

    return games.filter(function (game) {
        return game.id_comp === detail.Info.comp_id;
    });
}

function getPlays(detail: GameDetail): GamePlay[] {
    if (!detail.Jugadas) {
        return [];
    }

    return Array.isArray(detail.Jugadas)
        ? detail.Jugadas
        : Object.values(detail.Jugadas);
}

function getPlayer(players: PlayersById, playerId: string) {
    return players[playerId];
}

function getPlayerName(players: PlayersById, playerId: string): string {
    return getPlayer(players, playerId)?.nombre || "Jugador sin nombre";
}

const POSITION_LABELS: Record<string, string> = {
    "1": "QB",
    "2": "RB",
    "3": "WR",
    "4": "DL",
    "5": "LB",
    "6": "DB",
    "7": "K",
    "8": "OL"
};

function getPosition(
    players: PlayersById,
    playerId: string
): string {
    const player = getPlayer(
        players,
        playerId
    ) as Record<string, unknown> | undefined;

    if (!player) {
        return "—";
    }

    const positionIndex = String(
        player.pos ??
            player.posicion ??
            ""
    ).trim();

    return POSITION_LABELS[positionIndex] || "—";
}


function getPlayerImage(
    players: PlayersById,
    playerId: string
): string | undefined {
    return getPlayer(players, playerId)?.img;
}

function getTotalTackles(player: DefensiveStats): number {
    return player.tkl + player.assTkl;
}

function getTotalSacks(player: DefensiveStats): string {
    return (player.sack + player.asSack / 2).toFixed(1);
}

function getReturnYards(player: PlayerStats): number {
    return player.ydsK + player.ydsP;
}

function getKickerPoints(player: KickerStats): number {
    return player.compFG * 3 + player.compXP;
}

const TeamStats: React.FC = () => {
    const { id } = useParams<RouteParams>();
    const history = useHistory();

    const [teamDetail, setTeamDetail] =
        useState<TeamDetail | null>(null);
    const [players, setPlayers] = useState<PlayersById>({});
    const [statistics, setStatistics] =
        useState<TeamStatistics | null>(null);
    const [activeCategory, setActiveCategory] =
        useState<StatCategory>("pase");
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");

    const loadStatistics = useCallback(
        async function (forceRefresh = false) {
            try {
                setError("");

                if (forceRefresh) {
                    setRefreshing(true);
                } else {
                    setLoading(true);
                }

                const [detail, playersData] = await Promise.all([
                    getTeamDetail(id, forceRefresh),
                    getPlayers(forceRefresh)
                ]);

                const games = getGames(detail);

                const gameDetails = await Promise.all(
                    games.map(function (game) {
                        return getGameDetail(game.id, forceRefresh);
                    })
                );

                const offensive: PlayerStats[] = [];
                const defensive: DefensiveStats[] = [];
                const kickers: KickerStats[] = [];

                for (const gameDetail of gameDetails) {
                    const plays = getPlays(gameDetail);

                    for (const play of plays) {
                        const isOffensivePlay = play.ofensiva === id;
                        const isSpecialTeamsReturn =
    Boolean(play.k?.trim()) &&
    Boolean(play.ret?.trim());


                        if (isOffensivePlay) {
                            Components.getQB(play, offensive);
                            Components.getRB(play, offensive);
                            Components.getWR(play, offensive);
                            Components.getK(play, kickers);
                        }

                        if (!isOffensivePlay) {
                            Components.getSt(play, offensive);

                            if (
                                play.d !== "0" &&
                                !isSpecialTeamsReturn
                            ) {
                                Components.getDEF(play, defensive);
                            }
                        } else if (
                            play.d === "0" ||
                            isSpecialTeamsReturn
                        ) {
                            Components.getDEF(play, defensive);
                        }
                    }
                }

                setTeamDetail(detail);
                setPlayers(playersData);
                setStatistics({
                    offensive,
                    defensive,
                    kickers
                });
            } catch (requestError) {
                console.error(
                    "No fue posible calcular las estadísticas:",
                    requestError
                );

                setError(
                    "No fue posible cargar las estadísticas del equipo."
                );
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [id]
    );

    useEffect(
        function () {
            loadStatistics();
        },
        [loadStatistics]
    );

    const sortedOffensive = useMemo(function () {
        if (!statistics) {
            return [];
        }

        if (activeCategory === "pase") {
            return [...statistics.offensive]
                .filter(function (player) {
                    return player.att > 0;
                })
                .sort(Components.compareydP);
        }

        if (activeCategory === "carrera") {
            return [...statistics.offensive]
                .filter(function (player) {
                    return player.attA > 0;
                })
                .sort(Components.compareydA);
        }

        if (activeCategory === "recepcion") {
            return [...statistics.offensive]
                .filter(function (player) {
                    return player.attR > 0;
                })
                .sort(Components.compareydR);
        }

        if (activeCategory === "retornos") {
            return [...statistics.offensive]
                .filter(function (player) {
                    return player.attK > 0 || player.attP > 0;
                })
                .sort(Components.compareSt);
        }

        return [];
    }, [activeCategory, statistics]);

    const sortedDefense = useMemo(function () {
        if (!statistics) {
            return [];
        }

        return [...statistics.defensive]
            .filter(function (player) {
                return (
                    player.tkl > 0 ||
                    player.assTkl > 0 ||
                    player.sack > 0 ||
                    player.asSack > 0 ||
                    player.int > 0 ||
                    player.ff > 0 ||
                    player.fr > 0
                );
            })
            .sort(Components.compareTkl);
    }, [statistics]);

    const sortedKickers = useMemo(function () {
        if (!statistics) {
            return [];
        }

        return [...statistics.kickers]
            .filter(function (player) {
                return (
                    player.attFG > 0 ||
                    player.attXP > 0 ||
                    player.punt > 0
                );
            })
            .sort(Components.compareK);
    }, [statistics]);

    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadStatistics(true);
        event.detail.complete();
    }

    function renderPlayer(
        playerId: string,
        position: string
    ): React.ReactNode {
        const image = getPlayerImage(players, playerId);

        return (
            <button
                className="team-stats-player"
                type="button"
                onClick={function () {
                    history.push(`/player/${playerId}`);
                }}
            >
                <IonAvatar>
                    {image ? (
                        <img
                            src={image}
                            alt={`Foto de ${getPlayerName(
                                players,
                                playerId
                            )}`}
                        />
                    ) : (
                        <span>
                            {getPlayerName(players, playerId)
                                .charAt(0)
                                .toUpperCase()}
                        </span>
                    )}
                </IonAvatar>

                <span>
                    <strong>
                        {getPlayerName(players, playerId)}
                    </strong>
                    <small>{position}</small>
                </span>
            </button>
        );
    }

    function renderTable(): React.ReactNode {
        if (activeCategory === "defensa") {
            return (
                <div className="team-stats-table-wrap">
                    <table className="team-stats-table">
                        <thead>
                            <tr>
                                <th>DEFENSA</th>
                                <th>TOT</th>
                                <th>SKS</th>
                                <th>FF</th>
                                <th>FR</th>
                                <th>INT</th>
                                <th>TFL</th>
                                <th>PD</th>
                            </tr>
                        </thead>

                        <tbody>
                            {sortedDefense.map(function (player) {
                                return (
                                    <tr key={player.id}>
                                        <td>
                                            {renderPlayer(
                                                player.id,
                                                getPosition(
                                                    players,
                                                    player.id
                                                )
                                            )}
                                        </td>
                                        <td>{getTotalTackles(player)}</td>
                                        <td>{getTotalSacks(player)}</td>
                                        <td>{player.ff}</td>
                                        <td>{player.fr}</td>
                                        <td>{player.int}</td>
                                        <td>{player.tklL}</td>
                                        <td>{player.pd}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {sortedDefense.length === 0 && (
                        <div className="team-stats-no-data">
                            No hay estadísticas defensivas disponibles.
                        </div>
                    )}
                </div>
            );
        }

        if (activeCategory === "patadas") {
            return (
                <div className="team-stats-table-wrap">
                    <table className="team-stats-table">
                        <thead>
                            <tr>
                                <th>PATEADOR</th>
                                <th>XP</th>
                                <th>FG</th>
                                <th>PUNT</th>
                                <th>AVG</th>
                                <th>LONG</th>
                                <th>PTS</th>
                            </tr>
                        </thead>

                        <tbody>
                            {sortedKickers.map(function (player) {
                                return (
                                    <tr key={player.id}>
                                        <td>
                                            {renderPlayer(
                                                player.id,
                                                getPosition(
                                                    players,
                                                    player.id
                                                )
                                            )}
                                        </td>
                                        <td>
                                            {player.compXP}/{player.attXP}
                                        </td>
                                        <td>
                                            {player.compFG}/{player.attFG}
                                        </td>
                                        <td>{player.punt}</td>
                                        <td>
                                            {Components.getAvg(
                                                player.ydsP,
                                                player.punt
                                            )}
                                        </td>
                                        <td>{player.ydsFG}</td>
                                        <td>
                                            {getKickerPoints(player)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {sortedKickers.length === 0 && (
                        <div className="team-stats-no-data">
                            No hay estadísticas de patadas disponibles.
                        </div>
                    )}
                </div>
            );
        }

        const headers =
            activeCategory === "pase"
                ? ["QB", "ATT", "CMP", "YDS", "TD", "INT", "QBR"]
                : activeCategory === "carrera"
                ? ["CORREDOR", "ATT", "YDS", "TD", "FUM"]
                : activeCategory === "recepcion"
                ? ["RECEPTOR", "TGT", "REC", "YDS", "TD", "FUM"]
                : ["RETORNADOR", "KR", "PR", "YDS", "TD"];

        return (
            <div className="team-stats-table-wrap">
                <table className="team-stats-table">
                    <thead>
                        <tr>
                            {headers.map(function (header) {
                                return <th key={header}>{header}</th>;
                            })}
                        </tr>
                    </thead>

                    <tbody>
                        {sortedOffensive.map(function (player) {
                            return (
                                <tr key={player.id}>
                                    <td>
                                        {renderPlayer(
                                            player.id,
                                            getPosition(
                                                players,
                                                player.id
                                            )
                                        )}
                                    </td>

                                    {activeCategory === "pase" && (
                                        <>
                                            <td>{player.att}</td>
                                            <td>{player.comp}</td>
                                            <td>{player.ydsPas}</td>
                                            <td>{player.tdsPas}</td>
                                            <td>{player.int}</td>
                                            <td>
                                                {Components.getQBR(
                                                    player.att,
                                                    player.comp,
                                                    player.ydsPas,
                                                    player.tdsPas,
                                                    player.int
                                                )}
                                            </td>
                                        </>
                                    )}

                                    {activeCategory === "carrera" && (
                                        <>
                                            <td>{player.attA}</td>
                                            <td>{player.ydsAca}</td>
                                            <td>{player.tdsAca}</td>
                                            <td>{player.fumble}</td>
                                        </>
                                    )}

                                    {activeCategory === "recepcion" && (
                                        <>
                                            <td>{player.attR}</td>
                                            <td>{player.compR}</td>
                                            <td>{player.ydsRec}</td>
                                            <td>{player.tdsRec}</td>
                                            <td>{player.fumble}</td>
                                        </>
                                    )}

                                    {activeCategory === "retornos" && (
                                        <>
                                            <td>{player.attK}</td>
                                            <td>{player.attP}</td>
                                            <td>{getReturnYards(player)}</td>
                                            <td>
                                                {player.tdsK + player.tdsP}
                                            </td>
                                        </>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                {sortedOffensive.length === 0 && (
                    <div className="team-stats-no-data">
                        No hay estadísticas disponibles para esta categoría.
                    </div>
                )}
            </div>
        );
    }

    if (loading) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
                                aria-label="Regresar"
                                onClick={function () {
                                    history.goBack();
                                }}
                            >
                                <IonIcon icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>

                        <IonTitle>Jugadores</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="team-stats-content">
                    <IonProgressBar type="indeterminate" />
                </IonContent>
            </IonPage>
        );
    }

    if (error || !teamDetail || !statistics) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
                                aria-label="Regresar"
                                onClick={function () {
                                    history.goBack();
                                }}
                            >
                                <IonIcon icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>

                        <IonTitle>Jugadores</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="team-stats-content">
                    <div className="team-stats-empty">
                        <IonIcon icon={americanFootballOutline} />
                        <strong>No fue posible cargar las estadísticas</strong>
                        <span>{error}</span>

                        <IonButton
                            onClick={function () {
                                loadStatistics(true);
                            }}
                        >
                            Reintentar
                        </IonButton>
                    </div>
                </IonContent>
            </IonPage>
        );
    }

    const info = teamDetail.Info;

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="team-stats-toolbar">
                    <IonButtons slot="start">
                        <IonButton
                            aria-label="Regresar al equipo"
                            onClick={function () {
                                history.push(`/team/${id}`);
                            }}
                        >
                            <IonIcon icon={arrowBackOutline} />
                        </IonButton>
                    </IonButtons>

                    <IonTitle>Jugadores</IonTitle>

                    <IonButtons slot="end">
                        <IonButton
                            aria-label="Actualizar estadísticas"
                            disabled={refreshing}
                            onClick={function () {
                                loadStatistics(true);
                            }}
                        >
                            <IonIcon icon={refreshOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="team-stats-content">
                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Calculando estadísticas..."
                    />
                </IonRefresher>

                <section
                    className="team-stats-hero"
                    style={
                        info.color
                            ? {
                                  background: `linear-gradient(135deg, ${info.color}, #173e70)`
                              }
                            : undefined
                    }
                >
                    {info.logo ? (
                        <img
                            src={info.logo}
                            alt={`Logo de ${info.nombre}`}
                        />
                    ) : (
                        <div className="team-stats-hero__placeholder">
                            {info.nombre?.charAt(0) || "E"}
                        </div>
                    )}

                    <div>
                        <span>ESTADÍSTICAS DE JUGADORES</span>
                        <h1>{info.nombre}</h1>
                        <p>{info.comp_nombre || "Temporada actual"}</p>
                    </div>
                </section>

                <section className="team-stats-navigation">
                    <IonButton
                        fill="outline"
                        onClick={function () {
                            history.push(`/team/${id}`);
                        }}
                    >
                        Calendario
                    </IonButton>

                    <IonButton
                        fill="outline"
                        onClick={function () {
                            history.push(`/table/${id}`);
                        }}
                    >
                        Tabla
                    </IonButton>

                    <IonButton>Jugadores</IonButton>

                    <IonButton
                        fill="outline"
                        onClick={function () {
                            history.push(`/teamStatsTeam/${id}`);
                        }}
                    >
                        Equipo
                    </IonButton>

                    <IonButton
                        fill="outline"
                        onClick={function () {
                            history.push(`/rosterTeam/${id}`);
                        }}
                    >
                        Plantel
                    </IonButton>
                </section>

                <section className="team-stats-section">
                    <div className="team-stats-heading">
                        <div>
                            <span>LÍDERES DEL EQUIPO</span>
                            <h2>
                                {categories.find(function (category) {
                                    return category.id === activeCategory;
                                })?.label || "Estadísticas"}
                            </h2>
                        </div>

                        <IonIcon icon={statsChartOutline} />
                    </div>

                    <div className="team-stats-tabs">
                        {categories.map(function (category) {
                            return (
                                <IonButton
                                    key={category.id}
                                    fill={
                                        activeCategory === category.id
                                            ? "solid"
                                            : "outline"
                                    }
                                    onClick={function () {
                                        setActiveCategory(category.id);
                                    }}
                                >
                                    {category.label}
                                </IonButton>
                            );
                        })}
                    </div>

                    <div className="team-stats-season">
                        <IonIcon icon={peopleOutline} />
                        <span>
                            Estadísticas acumuladas de todos los partidos
                            registrados.
                        </span>
                        <IonBadge>{getGames(teamDetail).length} partidos</IonBadge>
                    </div>

                    {renderTable()}
                </section>
            </IonContent>
        </IonPage>
    );
};

export default TeamStats;
